import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { Provider } from "react-redux";
import { store } from "./redux";
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material";

const theme = createTheme({
  typography: {
    fontFamily: "PT Sans, serif",
  },
  palette: {
    primary: {
      main: "#2A4876",
    },
    secondary: {
      main: "#7BB9D5",
    },
    crayola: {
      main: "#AAB6C8",
    },
    "dark-gunmetal": {
      main: "#152134",
    },
  },
  components: {
    // Name of the component ⚛️
    MuiButtonBase: {
      defaultProps: {
        // The props to apply
        style: {
          paddingTop: "1.2rem",
          paddingBottom: "1.2rem",
          fontSize: "1rem",
          borderRadius: "40px",
        },
        disableRipple: true, // No more ripple, on the whole application 💣!
      },
    },
  },
});

const render = () => {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <App />
          </ThemeProvider>
        </StyledEngineProvider>
      </Provider>
    </React.StrictMode>,
  );
};

/**
 * Mock API, for building against endpoints that do not exist yet.
 *
 * Opt-in rather than on by default in development. Most work here runs against
 * the real dev backend, and silently intercepting everyone's requests would be
 * a confusing way to break it. Set `VITE_ENABLE_MSW=true` in `.env.local` to
 * turn it on.
 *
 * `import.meta.env.DEV` is a compile-time constant, so this whole branch —
 * including the dynamic import — is removed from the production bundle. MSW
 * never ships.
 *
 * Rendering waits for the worker: start it after mount and a request fired
 * during the first render escapes to the network, which is the kind of failure
 * that only shows up intermittently.
 */
const startMocks = async () => {
  if (!import.meta.env.DEV) return;
  if (import.meta.env.VITE_ENABLE_MSW !== "true") return;

  const { worker } = await import("./mocks/browser");

  // Logged so it is obvious from the console whether this tab is being served
  // by the mock API. Without it, the only symptom of an unregistered worker is
  // a feature failing for reasons that look unrelated to mocking.
  console.info("[mocks] starting mock API (VITE_ENABLE_MSW=true)");

  await worker.start({
    // Everything not stubbed goes to the real API rather than erroring, so
    // enabling mocks for onboarding does not break the rest of the app.
    onUnhandledRequest: "bypass",
  });
};

// Catch before render, not after. `.finally(render)` re-throws a failed start
// as an unhandled rejection once rendering is done, which surfaces as a
// console error with no obvious cause. A worker that cannot start should log
// plainly and let the app come up against the real API.
startMocks()
  .catch((error) => {
    console.warn("Mock API failed to start; using the real API.", error);
  })
  .then(render);
